import { useMemo, useCallback } from 'react';

/**
 * Interface para unidade de medida
 */
export interface UnitMeasurement {
  id: string;
  nome: string;
  slug: string;
}

/**
 * Interface para conversões customizadas do produto
 * Exemplo: { "cx": { "pct": 12, "kg": 1.2, "und": 36 } }
 */
export interface UnitConversions {
  [sourceUnit: string]: {
    [targetUnit: string]: number;
  };
}

/**
 * Conversões padrão conhecidas entre unidades comuns
 * Cada chave é a unidade de origem, o valor é um objeto com unidades de destino e fatores de conversão
 */
const DEFAULT_CONVERSIONS: UnitConversions = {
  // Peso
  kg: { g: 1000, mg: 1000000 },
  g: { kg: 0.001, mg: 1000 },
  mg: { g: 0.001, kg: 0.000001 },
  
  // Distância
  km: { m: 1000, cm: 100000, mm: 1000000 },
  m: { km: 0.001, cm: 100, mm: 1000 },
  cm: { m: 0.01, km: 0.00001, mm: 10 },
  mm: { cm: 0.1, m: 0.001, km: 0.000001 },
  
  // Volume
  L: { mL: 1000 },
  mL: { L: 0.001 },
  
  // Tempo
  h: { min: 60, s: 3600 },
  min: { h: 1/60, s: 60 },
  s: { min: 1/60, h: 1/3600 },
};

/**
 * Hook para conversão de unidades de medida
 */
export function useUnitConversion() {
  /**
   * Busca o fator de conversão entre duas unidades
   * @param fromSlug - Slug da unidade de origem
   * @param toSlug - Slug da unidade de destino
   * @param customConversions - Conversões customizadas do produto (opcional)
   * @returns Fator de conversão ou null se não encontrado
   */
  const getConversionFactor = useCallback(
    (
      fromSlug: string,
      toSlug: string,
      customConversions?: UnitConversions
    ): number | null => {
      // Se as unidades são iguais, o fator é 1
      if (fromSlug === toSlug) {
        return 1;
      }

      // Primeiro, tenta nas conversões customizadas
      if (customConversions && customConversions[fromSlug]) {
        const factor = customConversions[fromSlug][toSlug];
        if (factor !== undefined) {
          return factor;
        }
      }

      // Depois, tenta nas conversões padrão
      if (DEFAULT_CONVERSIONS[fromSlug]) {
        const factor = DEFAULT_CONVERSIONS[fromSlug][toSlug];
        if (factor !== undefined) {
          return factor;
        }
      }

      // Tenta conversão inversa nas conversões customizadas
      if (customConversions && customConversions[toSlug]) {
        const inverseFactor = customConversions[toSlug][fromSlug];
        if (inverseFactor !== undefined) {
          return 1 / inverseFactor;
        }
      }

      // Tenta conversão inversa nas conversões padrão
      if (DEFAULT_CONVERSIONS[toSlug]) {
        const inverseFactor = DEFAULT_CONVERSIONS[toSlug][fromSlug];
        if (inverseFactor !== undefined) {
          return 1 / inverseFactor;
        }
      }

      // Se não encontrou conversão, retorna null
      return null;
    },
    []
  );

  /**
   * Converte quantidade de uma unidade para outra
   * @param quantity - Quantidade na unidade de origem
   * @param fromSlug - Slug da unidade de origem
   * @param toSlug - Slug da unidade de destino
   * @param customConversions - Conversões customizadas do produto (opcional)
   * @returns Quantidade convertida ou null se conversão não encontrada
   */
  const convertQuantity = useCallback(
    (
      quantity: number,
      fromSlug: string,
      toSlug: string,
      customConversions?: UnitConversions
    ): number | null => {
      const factor = getConversionFactor(fromSlug, toSlug, customConversions);
      if (factor === null) {
        return null;
      }
      return quantity * factor;
    },
    [getConversionFactor]
  );

  /**
   * Calcula o preço de um produto baseado na conversão de unidades
   * @param basePricePerUnit - Preço por unidade de compra (ex: R$ 50/kg)
   * @param quantityInSaleUnit - Quantidade na unidade de venda (ex: 200g)
   * @param purchaseUnitSlug - Slug da unidade de compra (ex: 'kg')
   * @param saleUnitSlug - Slug da unidade de venda (ex: 'g')
   * @param customConversions - Conversões customizadas do produto (opcional)
   * @returns Preço calculado ou null se conversão não disponível
   */
  const calculatePrice = useCallback(
    (
      basePricePerUnit: number,
      quantityInSaleUnit: number,
      purchaseUnitSlug: string,
      saleUnitSlug: string,
      customConversions?: UnitConversions
    ): number | null => {
      // Converte a quantidade da unidade de venda para a unidade de compra
      const quantityInPurchaseUnit = convertQuantity(
        quantityInSaleUnit,
        saleUnitSlug,
        purchaseUnitSlug,
        customConversions
      );

      if (quantityInPurchaseUnit === null) {
        console.warn(
          `Conversão não disponível: ${saleUnitSlug} -> ${purchaseUnitSlug}`
        );
        return null;
      }

      // Calcula o preço proporcional
      return basePricePerUnit * quantityInPurchaseUnit;
    },
    [convertQuantity]
  );

  /**
   * Verifica se uma conversão entre duas unidades está disponível
   * @param fromSlug - Slug da unidade de origem
   * @param toSlug - Slug da unidade de destino
   * @param customConversions - Conversões customizadas do produto (opcional)
   * @returns true se conversão está disponível
   */
  const isConversionAvailable = useCallback(
    (
      fromSlug: string,
      toSlug: string,
      customConversions?: UnitConversions
    ): boolean => {
      return getConversionFactor(fromSlug, toSlug, customConversions) !== null;
    },
    [getConversionFactor]
  );

  return {
    getConversionFactor,
    convertQuantity,
    calculatePrice,
    isConversionAvailable,
  };
}
